// 🔧 help.js
// ✅ Purpose: Public-facing Help / FAQ page for Let's Go Vote
// ✅ Features:
// - Provides searchable-style FAQ content organized by topic
// - Uses Bootstrap cards and accordion layout
// - Public and SEO-friendly
// - Covers voting, entries, sponsors, volunteers, auctions, accounts, privacy, and managers
// - Includes clear support/contact CTA at the bottom
// - Follows Let's Go Vote responsive layout standards
// - Fully block-commented for clarity and future devs

import React from "react";
import { Link } from "react-router-dom";
import SeoHead from "../../components/seo_head";

function Help() {
  // ✅ FAQ content is grouped by user-friendly topics.
  // Keeping this as structured data makes it easy to expand later,
  // move into Firestore later, or generate FAQ schema later.
  const faqSections = [
    {
      title: "Voting",
      icon: "🗳️",
      items: [
        {
          question: "How do I vote in an event?",
          answer:
            "Most events use QR codes or a direct voting link. Once voting opens, you can select your favorite entries and submit your ballot based on the voting rules chosen by the event organizer.",
        },
        {
          question: "Can I vote more than once?",
          answer:
            "Most events allow one vote per category. Some events may use different voting systems such as People’s Choice Voting, Ranked Choice Voting, or Judged Scorecards.",
        },
        {
          question: "Why can't I vote yet?",
          answer:
            "Voting may not be open yet, the event may have ended, or your account may need email verification before voting is allowed.",
        },
        {
          question: "How do I know my vote was counted?",
          answer:
            "After submitting a vote, Let's Go Vote displays a confirmation message. Event organizers can also review audit records to verify voting activity.",
        },
        {
          question: "Can event organizers change my vote?",
          answer:
            "Votes are stored with audit information and are not designed to be casually edited through normal event management screens.",
        },
      ],
    },
    {
      title: "Event Entries",
      icon: "🚗",
      items: [
        {
          question: "How do I submit an entry?",
          answer:
            "Some events allow public entry submissions. Visit the event's Entries page and select Submit Entry when submissions are open.",
        },
        {
          question: "Why is my entry pending approval?",
          answer:
            "Many events require organizer approval before entries become visible to voters. This helps keep event content accurate and appropriate.",
        },
        {
          question: "How many photos can I upload?",
          answer:
            "Let's Go Vote supports multiple photos per entry. Upload limits may vary based on the event setup.",
        },
        {
          question: "Can I edit my entry later?",
          answer:
            "Depending on event settings, organizers may allow updates before voting begins. If you need help updating an entry, contact the event organizer.",
        },
      ],
    },
    {
      title: "Sponsors",
      icon: "⭐",
      items: [
        {
          question: "How do sponsors appear on event pages?",
          answer:
            "Sponsors can be grouped into sponsor levels and displayed on public event pages with logos, descriptions, links, and sponsor details.",
        },
        {
          question: "What are sponsor awards?",
          answer:
            "Sponsors may present special awards to entries. Award winners can be displayed publicly after the event when enabled by the organizer.",
        },
      ],
    },
    {
      title: "Volunteers",
      icon: "🙋",
      items: [
        {
          question: "How do I sign up to volunteer?",
          answer:
            "Visit the Volunteer Opportunities page for the event and select an available time slot.",
        },
        {
          question: "Can I change my volunteer assignment?",
          answer:
            "Contact the event organizer if you need to change or cancel a volunteer commitment.",
        },
      ],
    },
    {
      title: "Silent Auctions",
      icon: "🏆",
      items: [
        {
          question: "How do I place a bid?",
          answer:
            "Open an auction item and submit a bid that meets the minimum bid requirements shown on the item page.",
        },
        {
          question: "What happens if I am outbid?",
          answer:
            "If outbid notifications are enabled, you may receive an email when another bidder places a higher bid.",
        },
        {
          question: "How do I know if I won?",
          answer:
            "Winning bids are determined when the auction closes. Event organizers may contact winners directly with next steps.",
        },
        {
          question: "How do Auction SMS alerts work?",
          answer:
            "Auction SMS alerts are optional transactional updates for outbid, winning, payment received, and pickup-ready status. Reply STOP to opt out at any time. Reply HELP for help, or use the Contact Support link on this page. See SMS Alerts Terms for details.",
        },
      ],
    },
    {
      title: "Account & Login",
      icon: "🔐",
      items: [
        {
          question: "Why do I need to verify my email?",
          answer:
            "Email verification helps protect voting integrity and reduces fraudulent account creation.",
        },
        {
          question: "Can I sign in with Google or Facebook?",
          answer:
            "Yes. Let's Go Vote supports multiple sign-in methods including Google and Facebook.",
        },
        {
          question: "I forgot my password. What should I do?",
          answer:
            "Use the password reset option on the login page to receive password reset instructions.",
        },
      ],
    },
    {
      title: "Privacy & Security",
      icon: "🛡️",
      items: [
        {
          question: "How is voting protected?",
          answer:
            "Let's Go Vote uses secure authentication, encrypted connections, audit logging, and event-level permissions to help protect voting integrity.",
        },
        {
          question: "Can organizers see my password?",
          answer:
            "No. Passwords are never visible to event organizers or Let's Go Vote administrators.",
        },
        {
          question: "How is my information stored?",
          answer:
            "Let's Go Vote uses secure cloud infrastructure and authenticated access controls to help protect user data.",
        },
      ],
    },
    {
      title: "Event Managers",
      icon: "⚙️",
      items: [
        {
          question: "How long does it take to set up an event?",
          answer:
            "Many events can be configured quickly once the organizer has categories, entries, dates, and voting rules ready.",
        },
        {
          question: "Can I create my own categories?",
          answer:
            "Yes. Event managers can create custom categories based on their event needs.",
        },
        {
          question: "Can I accept online entries?",
          answer:
            "Yes. Public entry submissions can be enabled through event settings.",
        },
        {
          question: "Can I use QR codes?",
          answer:
            "Yes. QR codes can be generated for voting, entries, sponsors, auctions, and other event features.",
        },
        {
          question: "Can I publish winners?",
          answer:
            "Yes. Event managers can control when winners and leaderboard results are shown publicly.",
        },
      ],
    },
  ];


  return (
    <section id="top-content" className="container-fluid py-5">
      <SeoHead
        title="Help Center | Let's Go Vote"
        description="Find answers to common questions about Let's Go Vote, including QR code voting, event entries, volunteers, sponsors, silent auctions, account access, and event management."
        path="/help"
        breadcrumbs={[
          { name: "Home", path: "/" },
          { name: "Help", path: "/help" },
        ]}
      />
      <div className="row mb-4">
        <div className="col text-start">
          <h1 className="display-5">❓ Help Center</h1>
          <p className="lead text-muted">
            Answers for voters, participants, sponsors, volunteers, and event managers.
          </p>
        </div>
      </div>

      <div className="white-section p-4 rounded shadow-sm bg-white">
        {/* ✅ Top Help Intro */}
        <section className="mb-5">
          <div className="alert alert-info text-start mb-4">
            <h5 className="mb-3">👋 How can we help?</h5>

            <p className="mb-3">
              Find answers to common questions about QR code voting, event
              entries, sponsors, volunteers, silent auctions, account access,
              and event management.
            </p>

            <div className="d-flex flex-column flex-md-row gap-2">
              <Link to="/contact" className="btn btn-primary">
                Contact Support
              </Link>

              <Link to="/request_event" className="btn btn-outline-primary">
                📬 Request to use Let&apos;s Go Vote at your own event
              </Link>
            </div>
          </div>
        </section>

        {/* ✅ Popular Help Topics */}
        <section className="mb-5">
          <h3 className="mb-3">Popular Help Topics</h3>

          <div className="row g-3">
            <div className="col-12 col-md-6 col-lg-3">
              <div className="border rounded p-3 h-100 bg-light">
                <h5 className="fw-bold">🗳️ Voting</h5>
                <p className="text-muted mb-0">
                  Questions about ballots, QR codes, vote confirmation, and voting rules.
                </p>
              </div>
            </div>

            <div className="col-12 col-md-6 col-lg-3">
              <div className="border rounded p-3 h-100 bg-light">
                <h5 className="fw-bold">🚗 Entries</h5>
                <p className="text-muted mb-0">
                  Help with public submissions, photos, approvals, and entry visibility.
                </p>
              </div>
            </div>

            <div className="col-12 col-md-6 col-lg-3">
              <div className="border rounded p-3 h-100 bg-light">
                <h5 className="fw-bold">🔐 Accounts</h5>
                <p className="text-muted mb-0">
                  Login, email verification, password reset, Google sign-in, and Facebook sign-in.
                </p>
              </div>
            </div>

            <div className="col-12 col-md-6 col-lg-3">
              <div className="border rounded p-3 h-100 bg-light">
                <h5 className="fw-bold">⚙️ Managers</h5>
                <p className="text-muted mb-0">
                  Event setup, categories, QR codes, winners, sponsors, volunteers, and auctions.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* ✅ FAQ Accordion Sections */}
        <section className="mb-5">
          <h3 className="mb-3">Frequently Asked Questions</h3>

          {faqSections.map((section, sectionIndex) => (
            <div className="mb-4" key={section.title}>
              <h4 className="mb-3">
                {section.icon} {section.title}
              </h4>

              <div className="accordion" id={`helpAccordion-${sectionIndex}`}>
                {section.items.map((item, itemIndex) => {
                  const accordionId = `section-${sectionIndex}-item-${itemIndex}`;
                  const headingId = `${accordionId}-heading`;
                  const collapseId = `${accordionId}-collapse`;

                  return (
                    <div className="accordion-item" key={item.question}>
                      <h3 className="accordion-header" id={headingId}>
                        <button
                          className={`accordion-button ${
                            itemIndex === 0 ? "" : "collapsed"
                          }`}
                          type="button"
                          data-bs-toggle="collapse"
                          data-bs-target={`#${collapseId}`}
                          aria-expanded={itemIndex === 0 ? "true" : "false"}
                          aria-controls={collapseId}
                        >
                          {item.question}
                        </button>
                      </h3>

                      <div
                        id={collapseId}
                        className={`accordion-collapse collapse ${
                          itemIndex === 0 ? "show" : ""
                        }`}
                        aria-labelledby={headingId}
                        data-bs-parent={`#helpAccordion-${sectionIndex}`}
                      >
                        <div className="accordion-body text-muted">
                          {item.answer}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </section>

        {/* ✅ Still Need Help */}
        <section className="mb-0">
          <div className="alert alert-primary text-start mb-0">
            <h5 className="mb-3">Still need help?</h5>

            <p className="mb-3">
              If you cannot find the answer you are looking for, contact the
              Let&apos;s Go Vote team and we&apos;ll be happy to help.
            </p>

            <Link to="/contact" className="btn btn-primary">
              Contact Support
            </Link>
          </div>
        </section>
      </div>
    </section>
  );
}

export default Help;
